import { useState } from "react";
import I18n from "common/i18n";
import Constants from "common/constant";
import { btnDelay, usePlaySound } from "common/utils";
import { PieceStatus } from "features/builder/types";
import { ProjectUser } from "features/creator/elements";
import { MockPiece, PieceListContext, PieceCardAction } from "features/builder/pieceList";

const FALLBACK_IMAGE = `${Constants.assetHost}/assets/images/builder_default_display.webp`;
const DEFAULT_USER_ICON = `${Constants.assetHost}/assets/users/icons/icon_default_kage01-1.png`;

// 1000 以上は 1.2k 表記
const formatCount = (n: number): string => {
  if (n < 1000) return String(n);
  const v = Math.floor(n / 100) / 10;
  return `${v}k`;
};

export type CardButton = {
  labelKey: string;
  icon?: string;
  action: PieceCardAction;
  className: string;
  disabled?: boolean;
};

export type CardVariant = {
  borderClass: string;
  imageFilterClass: string;
  cardOpacityClass: string;
  statusPill: { labelKey: string; pillClass: string; icon: string } | null;
  showMeta: boolean;
  showOverflow: boolean;
  publish: CardButton | null;
  left: CardButton | null;
  right: CardButton | null;
};

const PLAY_BTN: CardButton = {
  labelKey: "MSG_BUILDER_BTN_PLAY",
  icon: "play_arrow",
  action: PieceCardAction.PLAY,
  className: "btn-secondary",
};

// MINE タブ専用：公開/非公開トグルボタン
// 完成済み（completed）でないと公開できない。ただしすでに公開中なら非公開操作は常に許可
export const minePublishBtn = (piece: MockPiece): CardButton => {
  if (piece.published) {
    return {
      labelKey: "MSG_BUILDER_BTN_UNPUBLISH",
      icon: "lock",
      action: PieceCardAction.TOGGLE_PUBLISH,
      className: "btn-gray",
    };
  }
  const disabled = !piece.completed;
  return {
    labelKey: "MSG_BUILDER_BTN_PUBLISH",
    icon: "public",
    action: PieceCardAction.TOGGLE_PUBLISH,
    className: disabled ? "btn-gray" : "btn-secondary",
    disabled,
  };
};

export const getCardVariant = (piece: MockPiece, context: PieceListContext): CardVariant => {
  const isRemix = piece.copied_piece_id != null;
  const isOfficial = piece.is_official;

  if (context === PieceListContext.TRASH) {
    return {
      borderClass: "border-8 border-brown-80",
      imageFilterClass: "[filter:grayscale(0.6)] opacity-70",
      cardOpacityClass: "opacity-[0.85]",
      statusPill: null,
      showMeta: true,
      showOverflow: false,
      publish: null,
      left: {
        labelKey: "MSG_BUILDER_BTN_RESTORE",
        icon: "undo",
        action: PieceCardAction.RESTORE,
        className: "btn-primary",
      },
      right: {
        labelKey: "MSG_BUILDER_BTN_DELETE_FOREVER",
        icon: "delete_forever",
        action: PieceCardAction.DELETE_FOREVER,
        className: "btn-danger",
      },
    };
  }

  if (context === PieceListContext.PUBLIC) {
    return {
      borderClass: isOfficial
        ? "border-8 border-vivid-orange shadow-[0_0_12px_rgba(255,140,26,0.4)]"
        : isRemix
          ? "border-8 border-purple"
          : "border-8 border-blue-100",
      imageFilterClass: "",
      cardOpacityClass: "",
      statusPill: isOfficial
        ? {
            labelKey: "MSG_BUILDER_CARD_STATUS_OFFICIAL",
            pillClass: "bg-vivid-orange",
            icon: "verified",
          }
        : isRemix
          ? { labelKey: "MSG_BUILDER_CARD_STATUS_REMIX", pillClass: "bg-purple", icon: "cached" }
          : {
              labelKey: "MSG_BUILDER_CARD_STATUS_ORIGINAL",
              pillClass: "bg-blue-100",
              icon: "auto_awesome",
            },
      showMeta: true,
      showOverflow: true,
      publish: null,
      left: PLAY_BTN,
      // 作者がコピーを許可していないピースは、そもそもコピーボタンを出さない
      right: piece.enable_copied
        ? {
            labelKey: "MSG_BUILDER_BTN_COPY",
            icon: "cached",
            action: PieceCardAction.COPY,
            className: "btn-purple",
          }
        : null,
    };
  }

  // PieceListContext.MINE
  // 枠線色は公開/非公開で統一（緑=公開 / グレー=非公開）。ステータスは PieceStatusBadges で別途表示
  const mineBorder = piece.published ? "border-8 border-green-100" : "border-8 border-[#9CA3AF]";
  const publish = minePublishBtn(piece);

  // 管理画面で処分されたピース。一覧から消さずに、理由が分かるようにバッジを出す。
  // 公開はできないので公開ボタンは出さない（編集とあそぶは残す）
  if (piece.banned) {
    return {
      borderClass: "border-8 border-danger",
      imageFilterClass: "[filter:grayscale(1)]",
      cardOpacityClass: "",
      statusPill: {
        labelKey: "MSG_BUILDER_CARD_STATUS_BLOCKED",
        pillClass: "bg-danger",
        icon: "block",
      },
      showMeta: true,
      showOverflow: true,
      publish: null,
      left: PLAY_BTN,
      right: {
        labelKey: "MSG_BUILDER_MAP_EDIT_BTN",
        icon: "edit",
        action: PieceCardAction.EDIT,
        className: "btn-primary",
      },
    };
  }
  switch (piece.status) {
    case PieceStatus.DRAFT_NEW:
      return {
        borderClass: mineBorder,
        imageFilterClass: "",
        cardOpacityClass: "",
        statusPill: null,
        showMeta: true,
        showOverflow: true,
        publish,
        // 詳細画面では つくりかけ でも あそべる。一覧だけ押せないのは辻褄が合わない
        left: PLAY_BTN,
        right: {
          labelKey: "MSG_BUILDER_BTN_CONTINUE",
          icon: "edit",
          action: PieceCardAction.EDIT,
          className: "btn-primary",
        },
      };
    case PieceStatus.EDITING:
      return {
        borderClass: mineBorder,
        imageFilterClass: "",
        cardOpacityClass: "",
        statusPill: null,
        showMeta: true,
        showOverflow: true,
        publish,
        left: {
          labelKey: "MSG_BUILDER_BTN_PUBLISHED_NOW",
          icon: "play_arrow",
          action: PieceCardAction.PLAY,
          className: "btn-secondary",
        },
        right: {
          labelKey: "MSG_BUILDER_BTN_RESUME",
          icon: "edit",
          action: PieceCardAction.EDIT,
          className: "btn-primary",
        },
      };
    case PieceStatus.UNPUBLISHED:
      return {
        borderClass: mineBorder,
        imageFilterClass: "[filter:grayscale(0.3)]",
        cardOpacityClass: "",
        statusPill: null,
        showMeta: true,
        showOverflow: true,
        publish,
        left: PLAY_BTN,
        right: {
          labelKey: "MSG_BUILDER_MAP_EDIT_BTN",
          icon: "edit",
          action: PieceCardAction.EDIT,
          className: "btn-primary",
        },
      };
    default:
      // PUBLISHED
      return {
        borderClass: mineBorder,
        imageFilterClass: "",
        cardOpacityClass: "",
        statusPill: null,
        showMeta: true,
        showOverflow: true,
        publish,
        left: PLAY_BTN,
        right: {
          labelKey: "MSG_BUILDER_MAP_EDIT_BTN",
          icon: "edit",
          action: PieceCardAction.EDIT,
          className: "btn-primary",
        },
      };
  }
};

// ステータスバッジ1つ分（アイコン + ラベル）。既存ピルと同じ見た目トークンを流用。
// iconOnly のときはラベルを省きアイコンのみの円形バッジにする（2列・リスト表示用）
export const StatusBadge = ({
  icon,
  labelKey,
  pillClass,
  textClass,
  iconOnly,
}: {
  icon: string;
  labelKey: string;
  pillClass: string;
  textClass: string;
  iconOnly: boolean;
}) => (
  <div
    aria-label={iconOnly ? I18n.t(labelKey) : undefined}
    className={`flex-row-center shrink-0 rounded-full ${pillClass} ${
      iconOnly ? "w-10 h-10" : "gap-1 px-2"
    }`}
  >
    <span className={`material-symbols-outlined ${iconOnly ? "type-md" : "type-sm"} ${textClass}`}>
      {icon}
    </span>
    {!iconOnly && (
      <p className={`type-text type-sm whitespace-nowrap ${textClass}`}>{I18n.t(labelKey)}</p>
    )}
  </div>
);

// 公開/非公開を常時、つくりかけ(未完成)・編集中(has_draft)は該当時のみ表示（MINE・TRASH 用）。
// iconOnly=true で 2列・リスト表示時はアイコンのみにする
export const PieceStatusBadges = ({
  piece,
  iconOnly = false,
}: {
  piece: MockPiece;
  iconOnly?: boolean;
}) => {
  const isEditing = piece.status === PieceStatus.EDITING;
  return (
    <div className="flex-row-el flex-wrap items-center gap-1">
      {/* 管理画面で処分されたピース。理由が分かるように、いちばん先に出す */}
      {piece.banned && (
        <StatusBadge
          icon="block"
          labelKey="MSG_BUILDER_CARD_STATUS_BLOCKED"
          pillClass="bg-danger"
          textClass="text-white"
          iconOnly={iconOnly}
        />
      )}
      {!piece.completed && (
        <StatusBadge
          icon="pending"
          labelKey="MSG_BUILDER_CARD_STATUS_INCOMPLETE"
          pillClass="bg-beige-60"
          textClass="text-textcolor-black/70"
          iconOnly={iconOnly}
        />
      )}
      {piece.published ? (
        <StatusBadge
          icon="public"
          labelKey="MSG_BUILDER_CARD_STATUS_PUBLISHED"
          pillClass="bg-green-100"
          textClass="text-white"
          iconOnly={iconOnly}
        />
      ) : (
        <StatusBadge
          icon="lock"
          labelKey="MSG_BUILDER_CARD_STATUS_UNPUBLISHED"
          pillClass="bg-[#9CA3AF]"
          textClass="text-white"
          iconOnly={iconOnly}
        />
      )}
      {isEditing && (
        <StatusBadge
          icon="edit"
          labelKey="MSG_BUILDER_CARD_STATUS_EDITING"
          pillClass="bg-[#9BBFD4]"
          textClass="text-white"
          iconOnly={iconOnly}
        />
      )}
      {piece.copied_piece_id != null && (
        <StatusBadge
          icon="cached"
          labelKey="MSG_BUILDER_CARD_BADGE_REMIX"
          pillClass="bg-beige-60"
          textClass="text-textcolor-black/70"
          iconOnly={iconOnly}
        />
      )}
    </div>
  );
};

export const MapCard = ({
  id,
  piece,
  context,
  onAction,
  compact = false,
  heroPlay = false,
}: {
  // ガイドのスポットライト対象にするときだけ渡す
  id?: string;
  piece: MockPiece;
  context: PieceListContext;
  onAction: (action: PieceCardAction) => void;
  compact?: boolean;
  heroPlay?: boolean;
}) => {
  const play = usePlaySound();
  const [hideOverlays, setHideOverlays] = useState(false);
  const variant = getCardVariant(piece, context);

  // 1列のときは「うごかす」を光沢の大きな丸ボタンとして画像に載せる（再生アクションのみ）
  const showHeroPlay =
    heroPlay && variant.left != null && variant.left.action === PieceCardAction.PLAY;

  const fire = (action: PieceCardAction) => {
    play();
    btnDelay(() => onAction(action));
  };

  // 画像タップでオーバーレイをスライド開閉（詳細遷移はしない）
  const handleCard = () => {
    play();
    setHideOverlays((v) => !v);
  };

  const handleButton =
    (action: PieceCardAction, disabled?: boolean) => (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      if (disabled) return;
      fire(action);
    };

  const isEditing =
    context === PieceListContext.MINE &&
    (piece.status === PieceStatus.DRAFT_NEW || piece.status === PieceStatus.EDITING);
  const viewed = isEditing ? 0 : piece.viewed;
  const liked = isEditing ? 0 : piece.liked;
  const played = isEditing ? 0 : piece.played;

  const statusPillEl = variant.statusPill && (
    <StatusBadge
      icon={variant.statusPill.icon}
      labelKey={variant.statusPill.labelKey}
      pillClass={variant.statusPill.pillClass}
      textClass="text-white"
      iconOnly={compact}
    />
  );

  const authorEl = piece.author_name && (
    <div className="shrink-0">
      <ProjectUser name={piece.author_name} icon={piece.author_icon ?? DEFAULT_USER_ICON} />
    </div>
  );

  const renderButton = (btn: CardButton) =>
    compact ? (
      <button
        type="button"
        disabled={btn.disabled}
        onClick={handleButton(btn.action, btn.disabled)}
        aria-label={I18n.t(btn.labelKey)}
        className={`type-inherit-font btn ${btn.className} flex-col-center !flex-1 min-w-0 !h-12 !p-0`}
      >
        <span className="material-symbols-outlined text-white type-title">{btn.icon}</span>
      </button>
    ) : (
      <button
        type="button"
        disabled={btn.disabled}
        onClick={handleButton(btn.action, btn.disabled)}
        className={`type-inherit-font btn ${btn.className} !flex-1 min-w-0`}
      >
        <div className="flex-row-center gap-1">
          {btn.icon && (
            <span className="material-symbols-outlined text-white type-sm">{btn.icon}</span>
          )}
          <p className="type-text type-sm text-white whitespace-nowrap">{I18n.t(btn.labelKey)}</p>
        </div>
      </button>
    );

  const detailBtn = compact ? (
    <button
      type="button"
      onClick={handleButton(PieceCardAction.DETAIL)}
      aria-label={I18n.t("MSG_BUILDER_CARD_DETAIL_BTN")}
      className="type-inherit-font btn btn-primary-outlined flex-col-center !flex-1 min-w-0 !h-12 !p-0 !border-2 !border-solid !border-green-100"
    >
      <span className="material-symbols-outlined text-green-100 type-title">read_more</span>
    </button>
  ) : (
    <button
      type="button"
      onClick={handleButton(PieceCardAction.DETAIL)}
      className="type-inherit-font btn btn-primary-outlined !flex-1 min-w-0 !border-2 !border-solid !border-green-100"
    >
      <div className="flex-row-center gap-1">
        <span className="material-symbols-outlined text-green-100 type-sm">read_more</span>
        <p className="type-text type-sm text-green-100 whitespace-nowrap">
          {I18n.t("MSG_BUILDER_CARD_DETAIL_BTN")}
        </p>
      </div>
    </button>
  );

  const metaEl = variant.showMeta && (
    <div className="flex-row-el items-center gap-3 bg-white/80 rounded-full px-3 py-1">
      <div className="flex-row-el items-center gap-1">
        <span className="material-symbols-outlined text-[#4c9df1] type-sm">visibility</span>
        <p className="type-text type-sm text-textcolor-black">{formatCount(viewed)}</p>
      </div>
      <div className="flex-row-el items-center gap-1">
        <span className="material-symbols-outlined material-symbols-filled text-[#fc6560] type-sm">
          favorite
        </span>
        <p className="type-text type-sm text-textcolor-black">{formatCount(liked)}</p>
      </div>
      <div className="flex-row-el items-center gap-1">
        <span className="material-symbols-outlined text-green-100 type-sm">play_arrow</span>
        <p className="type-text type-sm text-textcolor-black">{formatCount(played)}</p>
      </div>
    </div>
  );

  return (
    <div
      id={id}
      onClick={handleCard}
      className={`relative box-border w-full ${compact ? "aspect-square" : "aspect-[4/3]"} overflow-hidden rounded-2xl bg-beige-60 border-solid shadow-md pointer-events-auto cursor-pointer ${variant.borderClass} ${variant.cardOpacityClass}`}
    >
      <img
        alt={piece.name}
        src={piece.cover ?? piece.capture ?? FALLBACK_IMAGE}
        onError={(e) => {
          (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
        }}
        className={`w-full h-full object-cover ${variant.imageFilterClass}`}
      />

      {/* 上ヘッダ（タップで上へスライドアウト） */}
      <div
        className={`flex-row-el items-center justify-between gap-3 absolute top-0 left-0 right-0 px-3 py-2 bg-white/80 z-[3] transition-all duration-300 ${
          hideOverlays ? "-translate-y-full opacity-0 pointer-events-none" : "opacity-100"
        }`}
      >
        <div className="flex-col-el items-start flex-1 min-w-0 gap-1">
          <p className="type-text type-body text-textcolor-black !text-left whitespace-nowrap truncate w-full">
            {piece.name}
          </p>
          {context === PieceListContext.PUBLIC ? (
            statusPillEl
          ) : (
            <PieceStatusBadges piece={piece} iconOnly={compact} />
          )}
        </div>
        {authorEl}
      </div>

      {/* 下オーバーレイ（数値・補足・ボタン。タップで下へスライドアウト） */}
      <div
        className={`flex-col-el items-start gap-2 absolute bottom-0 left-0 right-0 z-[3] transition-all duration-300 ${
          hideOverlays ? "translate-y-full opacity-0 pointer-events-none" : "opacity-100"
        }`}
      >
        <div className="flex-col-el items-start gap-2 w-full px-3">{metaEl}</div>

        <div className="flex-row-el flex-wrap justify-center items-center w-full gap-2 px-3 py-2 bg-white/80 upsilon">
          {variant.publish && renderButton(variant.publish)}
          {variant.left && !showHeroPlay && renderButton(variant.left)}
          {variant.right && renderButton(variant.right)}
          {context !== PieceListContext.TRASH && detailBtn}
        </div>
      </div>

      {showHeroPlay && variant.left && (
        <div
          className={`absolute bottom-24 right-4 z-[4] pointer-events-auto transition-all duration-300 ${
            hideOverlays ? "translate-y-full opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          <button
            type="button"
            disabled={variant.left.disabled}
            onClick={handleButton(variant.left.action, variant.left.disabled)}
            aria-label={I18n.t("MSG_BUILDER_MAP_RUN_BTN")}
            className={`type-inherit-font flex-col-center relative w-28 h-28 rounded-full border-4 border-solid border-white bg-gradient-to-b from-orange-80 to-orange-100 shadow-xl overflow-hidden cursor-pointer active:opacity-80 ${
              variant.left.disabled ? "opacity-60" : ""
            }`}
          >
            <div className="absolute left-3 right-3 top-2 h-2/5 rounded-full bg-white/50 blur-sm pointer-events-none" />
            <span className="material-symbols-outlined material-symbols-filled text-white type-hero relative z-10">
              play_arrow
            </span>
            <p className="type-text type-sm text-white relative z-10">
              {I18n.t("MSG_BUILDER_MAP_RUN_BTN")}
            </p>
          </button>
        </div>
      )}
    </div>
  );
};
